import {
  BaseProductDetailsFragment,
  ProductDetailsVariantFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { parsePriceVariant } from '@/ui/utils/parsePriceVariant';
import type { ICartState } from '@/ui/store/cart/types';
import { CartUpsellingItemSection } from '@/ui/components/layout/cart/CartAddOnServices/CartUpsellingItemSection/types/CartUpsellingItemSection';
import {
  CrystallizeProductTypeDefault,
  CrystallizeProductTypeValues,
} from '@/integrations/crystallize/types/constants';
import { DeliveryTimeRange } from '@/integrations/crystallize/types/localization/DeliveryTimeRange';
import { ParsedProductVariant } from '../components/pages/ProductDetail/types/ParsedProductVariant';
import { ManufacturerOption } from '../components/pages/ProductDetail/SmartlockDetail/DoorKnobSection/types/ManufacturerOption';
import { Bundle } from './BundleService';
import { SelectedProductVariants } from '../components/pages/ProductDetail/types/SelectedProductVariants';
import { parseWarehouseDetails } from '../components/pages/ProductDetail/utils/parseWarehouseDetails';
import { componentByTypename } from '../utils/componentByTypename';

/** A product variant, identified by its product and SKU. */
export interface ProductVariantReference {
  productId: string;
  sku: string;
}

export abstract class BaseProduct<
  T extends BaseProductDetailsFragment = BaseProductDetailsFragment,
  V extends ParsedProductVariant = ParsedProductVariant,
> {
  id: string;

  path: string;

  shape: string;

  name: string;

  variants: V[];

  protected cartStore: ICartState;

  constructor(data: T, cartStore: ICartState) {
    this.parseProductVariant = this.parseProductVariant.bind(this);

    this.cartStore = cartStore;

    this.id = data.id;
    this.path = data.path!;
    this.shape = data.shape.identifier;
    this.name = data.name!;
    this.variants = (data.variants?.map(this.parseProductVariant) as V[]) || [];
  }

  addItemsToCart(
    currentVariant: V,
    options?: {
      selectedBundle?: Bundle;
      selectedBundleProductVariants?: SelectedProductVariants;
      upgradeProgramDeviceId?: string;
      quantity?: number;
    },
  ): void {
    if (options && options.selectedBundle && options.selectedBundleProductVariants) {
      this.handleCartUpselling(options.selectedBundle.cartUpselling);
      this.addBundleToCart(
        options.selectedBundle,
        options.selectedBundleProductVariants,
        options.upgradeProgramDeviceId,
        options.quantity,
      );
    } else {
      this.addSingleItemToCart(currentVariant, options?.quantity);
    }
  }

  protected handleCartUpselling(cartUpselling: CartUpsellingItemSection[] | undefined): void {
    this.cartStore.setCartUpsellingData(cartUpselling);
    // Content deleted in Crystallize leaves an empty list behind, which has nothing to offer.
    this.cartStore.setShowCartUpselling(!!cartUpselling?.length);
  }

  protected addSingleItemToCart(currentVariant: V, quantity?: number): void {
    this.cartStore.addProduct(this, currentVariant, { quantity });
  }

  protected addBundleToCart(
    selectedBundle: Bundle,
    selectedBundleProductVariants: SelectedProductVariants,
    upgradeProgramDeviceId?: string,
    quantity?: number,
  ): void {
    const bundleCartItem = selectedBundle.getBundleCartItems(selectedBundleProductVariants);
    if (!bundleCartItem) {
      return;
    }
    const finalBundle = quantity && quantity > 0
      ? { ...bundleCartItem, quantity, upgradeProgramDeviceId }
      : { ...bundleCartItem, upgradeProgramDeviceId };
    this.cartStore.addBundle(finalBundle);
  }

  abstract addToCart(
    currentVariant: V,
    options?: {
      hasDoorKnob?: boolean;
      selectedManufacturer?: ManufacturerOption;
      selectedBundle?: Bundle;
      selectedBundleProductVariants?: SelectedProductVariants;
      activationCode?: string;
      nukiIds?: string[];
      directPurchase?: boolean;
      quantity?: number;
    },
  ): void;

  abstract isSkuInStock(sku: string): boolean;

  abstract isVariantInStock(productVariant: ParsedProductVariant): boolean;

  abstract isLowStock(productVariant: ParsedProductVariant): boolean;

  abstract getStandardDeliveryTimeRange(productVariant: ParsedProductVariant): DeliveryTimeRange;

  abstract getLowStockDeliveryTimeRange(productVariant: ParsedProductVariant): DeliveryTimeRange;

  abstract getDeliveryTimeRange(productVariant: ParsedProductVariant): DeliveryTimeRange;

  abstract get isAnyVariantInStock(): boolean;

  protected parseProductVariant(rawVariant: ProductDetailsVariantFragment): ParsedProductVariant {
    return {
      name: rawVariant.name!,
      sku: rawVariant.sku,
      isDefault: rawVariant.isDefault || false,
      priceVariant: parsePriceVariant(rawVariant.priceVariant),
      productType:
        (componentByTypename(rawVariant.productType?.content, 'SelectionContent')?.options?.[0]
          ?.key as CrystallizeProductTypeValues) || CrystallizeProductTypeDefault,
      images: rawVariant.images || [],
      videos: rawVariant.videos ?? [],
      warehouseDetails: parseWarehouseDetails(rawVariant.warehouseDetails) || null,
      stockCount: rawVariant.stock || null,
      isGlobal: componentByTypename(rawVariant.isGlobal?.content, 'BooleanContent')?.value || null,
    };
  }

  /** The variant as product rules and orders identify it. */
  getVariantReference(variant: ParsedProductVariant): ProductVariantReference {
    return { productId: this.id, sku: variant.sku };
  }

  getVariantBySKU(sku: string | null): V | null {
    if (!sku) {
      return null;
    }
    const searchedVariant = this.variants.find((variant) => variant.sku === sku);
    return searchedVariant || null;
  }

  getDefaultVariant(): V {
    const defaultVariant = this.variants.find((variant) => variant.isDefault);
    return defaultVariant || this.variants[0];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  findAttachedBundleByExtRef(extRef: string): Bundle | undefined {
    return undefined;
  }
}
